import { reportError, reportMessage, setUserContext } from './sentry';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (context && Object.keys(context).length > 0) {
      return `${prefix} ${message} ${JSON.stringify(context)}`;
    }

    return `${prefix} ${message}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.debug(this.formatMessage('debug', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    // Only log in development, not sent to Sentry
    if (this.isDevelopment) {
      console.info(this.formatMessage('info', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    // Only log in development, not sent to Sentry
    if (this.isDevelopment) {
      console.warn(this.formatMessage('warn', message, context));
    }
  }

  error(message: string, error?: Error | LogContext, context?: LogContext): void {
    // Handle different parameter combinations
    let errorObj: Error | undefined;
    let logContext: LogContext = context || {};

    if (error instanceof Error) {
      errorObj = error;
      logContext = { ...logContext, errorMessage: error.message, errorStack: error.stack };
    } else if (typeof error === 'object') {
      logContext = { ...logContext, ...error };
    }

    const logMessage = this.formatMessage('error', message, logContext);

    if (this.isDevelopment) {
      console.error(logMessage);
      if (errorObj) {
        console.error('Error object:', errorObj);
      }
    } else {
      // Send to Sentry in production
      if (errorObj) {
        reportError(errorObj, { message, ...logContext });
      } else {
        reportMessage(message, 'error', logContext);
      }
    }
  }

  // User-specific logging
  setUser(user: { id: string; email?: string; username?: string } | null): void {
    setUserContext(user);

    if (this.isDevelopment) {
      if (user) {
        console.info(`Logger: User context set - ${user.email || user.id}`);
      } else {
        console.info('Logger: User context cleared');
      }
    }
  }

  // Performance logging
  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string, message?: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.timeEnd(label);
      if (message) {
        this.info(message, context);
      }
    } else {
      // In production, you might want to track performance metrics
      if (message) {
        reportMessage(message, 'info', { ...context, performanceLabel: label });
      }
    }
  }

  // Track user actions
  trackAction(action: string, context?: LogContext): void {
    const message = `User action: ${action}`;

    if (this.isDevelopment) {
      this.info(message, context);
    } else {
      reportMessage(message, 'info', { action, ...context });
    }
  }

  // Track API calls
  trackApiCall(method: string, url: string, status: number, duration?: number, context?: LogContext): void {
    const logContext = {
      method,
      url,
      status,
      duration,
      ...context,
    };

    if (status >= 500) {
      // Only track server errors (5xx) as critical incidents
      this.error(`Server error: ${method} ${url}`, logContext);
    } else if (status >= 400) {
      // Client errors (4xx) only in development
      if (this.isDevelopment) {
        console.warn(this.formatMessage('warn', `Client error: ${method} ${url} (${status})`, logContext));
      }
    }
    // 2xx successes are not tracked
  }

  // Track business events - only in development
  trackEvent(event: string, properties?: LogContext): void {
    if (this.isDevelopment) {
      this.info(`Business event: ${event}`, properties);
    }
    // Business events are not sent to Sentry in production
  }

  // Report critical incidents manually - these go to Sentry in production
  critical(message: string, error?: Error | LogContext, context?: LogContext): void {
    let errorObj: Error | undefined;
    let logContext: LogContext = context || {};

    if (error instanceof Error) {
      errorObj = error;
      logContext = { ...logContext, errorMessage: error.message, errorStack: error.stack };
    } else if (typeof error === 'object') {
      logContext = { ...logContext, ...error };
    }

    const logMessage = this.formatMessage('critical', message, logContext);

    if (this.isDevelopment) {
      console.error(logMessage);
      if (errorObj) {
        console.error('Critical error object:', errorObj);
      }
    } else {
      // Send to Sentry as critical incident
      if (errorObj) {
        reportError(errorObj, { message, severity: 'critical', ...logContext });
      } else {
        reportMessage(message, 'error', { severity: 'critical', ...logContext });
      }
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for context
export type { LogContext };

// Export a hook for React components
export const useLogger = () => {
  return logger;
};